import { Component } from '@theme/component';
import {
  fetchConfig,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent, CartSectionRestoredEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  cartMutationQueue,
  isPendingControl,
  resolveMissingLine,
  resolveUnansweredChange,
  setControlsPending,
} from '@theme/cart-mutation-queue';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  /** @type {Set<Promise<void>>} */
  static #pendingQuantityUpdates = new Set();

  /**
   * Includes quantity mutations, their section renders, and control cleanup across all cart instances.
   * @returns {ReadonlyArray<Promise<void>>} A snapshot that cannot modify the shared pending set.
   */
  static get pendingQuantityUpdates() {
    return Array.from(CartItemsComponent.#pendingQuantityUpdates);
  }

  /** How long a quantity control must rest before its value is sent, so a run of clicks is one request. */
  static #QUANTITY_SETTLE_MS = 300;

  /**
   * Quantity changes waiting for their line's control to settle, by line key. Each line has its own timer,
   * so changing a second line does not discard the change just made to the first.
   * @type {Map<string, number>}
   */
  #settlingQuantities = new Map();

  /** The quantity being sent right now for each line, by line key. @type {Map<string, number>} */
  #sendingQuantities = new Map();

  /** Rows hidden ahead of the server confirming their removal, by line key. @type {Map<string, HTMLElement[]>} */
  #removedRows = new Map();

  /** Puts the line items back if the removal that emptied the cart on screen fails. @type {(() => void) | null} */
  #restoreEmptiedCart = null;

  /** Updates sent or waiting in this component. */
  #activeUpdates = 0;

  /** Cart changes made elsewhere on the page (such as an add to cart) that have not finished. */
  #externalUpdates = 0;

  /**
   * Set when the cart on screen may not match the server: one of our updates overlapped an outside change, or
   * a request went unanswered and the cart could not be read afterwards. Cleared only by a successful read.
   */
  #needsReconcile = false;

  /** That read, while it is under way. @type {Promise<boolean> | null} */
  #reconcilePromise = null;

  /** Keys of lines whose removal the server has confirmed to this component. @type {Set<string>} */
  #confirmedRemovals = new Set();

  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /**
   * Refreshes this section when a cart AJAX response cannot include rendered section HTML.
   * @param {Object} [options] - Additional section morph options.
   * @param {boolean} [options.injectStylesheet]
   */
  async #refreshCartSection(options = {}) {
    try {
      await sectionRenderer.renderSection(this.sectionId, {
        cache: false,
        mode: this.isDrawer ? 'hydration' : 'full',
        ...options,
      });
      this.#afterRender();
    } catch (error) {
      if (error?.name === 'AbortError') return;

      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Failed to refresh cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    }
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const isExternalCartUpdate = this.#isExternalCartUpdate(event);

    event.promise
      ?.then(({ detail }) => {
        if (!isExternalCartUpdate) return;

        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          sectionRenderer.abortRender(this.sectionId);
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          this.#refreshCartSection();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          sectionRenderer.abortRender(this.sectionId);
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          this.#refreshCartSection();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantitySelectorUpdate);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    window.addEventListener('pageshow', this.#handlePageShow);
    window.addEventListener('online', this.#handleOnline);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantitySelectorUpdate);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    window.removeEventListener('pageshow', this.#handlePageShow);
    window.removeEventListener('online', this.#handleOnline);

    for (const timer of this.#settlingQuantities.values()) clearTimeout(timer);
    this.#settlingQuantities.clear();
  }

  /**
   * Re-renders the cart section when the page is restored from the back/forward cache.
   *
   * A restored page replays a frozen DOM, so the line items still show whatever the cart
   * held before the shopper navigated away. `cart-icon.js` self-corrects its count bubble
   * on this same event, which is why the bubble and the cart body disagree until a reload.
   *
   * `persisted` alone does not cover Chrome. Chrome refuses the bfcache for any document holding
   * the Sign in with Shop iframe, `/services/login_with_shop/authorize` inside the shadow root of
   * `shopify-account > shop-login`, and every product page carries one. A shopper's Back there
   * rebuilds the document from the HTTP disk cache instead: `persisted` is false and the frozen
   * cart markup renders anyway. Only the navigation entry separates that from an ordinary page
   * view, so it is the second channel. Safari and Firefox do restore the same page and arrive
   * through `persisted`. Measured on os3 across all three browsers, 2026-09-03.
   *
   * Both channels are still needed. A restored document keeps its ORIGINAL navigation entry in
   * Chrome and Safari, reporting `navigate`, so `back_forward` alone would miss every real
   * restore. And `pageshow` fires after `load` on every ordinary navigation, so dropping both
   * guards would put a section fetch on every page view.
   *
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    const navigationEntry = globalThis.performance?.getEntriesByType?.('navigation')[0];
    const navigationType = CartItemsComponent.#isNavigationTiming(navigationEntry) ? navigationEntry.type : undefined;
    if (!event.persisted && navigationType !== 'back_forward') return;
    // `section_id` is optional in `snippets/cart-items-component.liquid` and the `sectionId`
    // getter throws without it. Every other caller reads it inside a promise chain that
    // already has a catch; this one runs straight off a window listener, so it would escape.
    if (!this.dataset.sectionId) return;

    // `cache: false` on both paths. A bfcache restore never fires `load`, so the renderer's cache
    // still holds the pre-navigation copy. A disk-cache Back does fire `load` and seeds the cache
    // from the stale document it just rebuilt. Either way the cached copy is the same stale HTML
    // the DOM already shows.
    sectionRenderer
      .renderSection(this.sectionId, {
        cache: false,
        mode: this.isDrawer ? 'hydration' : 'full',
        // Empty → non-empty adds the cart summary markup, which carries its own stylesheet.
        injectStylesheet: this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null,
      })
      .then(() => {
        // Morph swaps the quantity selectors in place, so they never reconnect and their
        // min/max button states stay bound to the pre-restore quantities. A line restored to its
        // minimum would otherwise keep minus enabled: a wrong affordance and a wasted cart mutation.
        this.#updateCartQuantitySelectorButtonStates();
        // No drawer-open event fires on a restore, so the sticky summary measurement has to
        // be driven from here. `cart-drawer-component` listens for this.
        this.dispatchEvent(new CartSectionRestoredEvent());
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] bfcache restore render failed:', error);
      });
  };

  /** The connection is back: read the cart if an earlier change could not be confirmed. */
  #handleOnline = () => {
    this.#reconcileIfNeeded();
  };

  /**
   * Waits for a line's quantity control to settle, then sends its latest value.
   * @param {Event} event
   */
  #handleQuantitySelectorUpdate = (event) => {
    if (!(event instanceof QuantitySelectorUpdateEvent)) return;
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    const row = this.#rowFor(event.target, line);
    const key = row?.dataset.key || `line:${line}`;

    clearTimeout(this.#settlingQuantities.get(key));
    this.#settlingQuantities.set(
      key,
      setTimeout(() => {
        this.#settlingQuantities.delete(key);
        this.#onQuantityChange(quantity, line, row);
      }, CartItemsComponent.#QUANTITY_SETTLE_MS)
    );
  };

  /**
   * Sends a settled quantity for a line.
   * @param {number} quantity - The quantity the line should have.
   * @param {number} line - The 1-based position the line had when it was rendered.
   * @param {HTMLElement | undefined} row - The line's row.
   */
  #onQuantityChange(quantity, line, row) {
    if (quantity === 0) {
      this.#removeLine(line, row);
      return;
    }

    const key = row?.dataset.key ?? '';
    const input = /** @type {HTMLInputElement | null | undefined} */ (row?.querySelector('input[data-cart-line]'));

    // Leaving the field, or stepping away and back, without a net change is not a cart update. The value
    // attribute is the quantity the server last confirmed.
    const queueKey = key || `line:${line}`;
    if (!cartMutationQueue.isWaiting(queueKey)) {
      const sending = this.#sendingQuantities.get(queueKey);
      // The same value is already on its way (a click on a stepper is followed by the field's own blur).
      if (sending === quantity) return;
      if (sending === undefined && input && Number(input.defaultValue) === quantity) return;
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
      key,
    });

    const textComponent = /** @type {TextComponent | null | undefined} */ (row?.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The 1-based position of the line item.
   * @param {Event} [event] - The click that asked for the removal.
   */
  onLineItemRemove(line, event) {
    const target = event?.target instanceof Element ? event.target : null;

    // While a change is being sent the control is shown as unavailable, to keyboard and pointer alike.
    const button = target?.closest('.cart-items__remove');
    if (isPendingControl(button)) return;

    this.#removeLine(line, this.#rowFor(target, line));
  }

  /**
   * Removes a line, hiding it at once and putting it back if the server does not confirm.
   * @param {number} line - The 1-based position the line had when it was rendered.
   * @param {HTMLElement | undefined} row - The line's row.
   */
  #removeLine(line, row) {
    const key = row?.dataset.key ?? '';

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
      key,
    });

    if (!row || !key || this.#removedRows.has(key)) return;

    const visibleRows = this.#visibleRows;
    const rowsToRemove = [
      row,
      // Get all nested lines of the row to remove
      ...this.#rows.filter((nestedRow) => nestedRow.dataset.parentKey === key),
    ];
    const index = visibleRows.indexOf(row);
    const hadFocus = rowsToRemove.some((removedRow) => removedRow.contains(document.activeElement));

    this.#removedRows.set(key, rowsToRemove);

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = visibleRows.every((visibleRow) => rowsToRemove.includes(visibleRow));

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);
      const lineItems = document.createDocumentFragment();
      let cancelled = false;
      let emptied = false;

      // The line items are kept, not discarded, so a failed removal can put them back.
      this.#restoreEmptiedCart = () => {
        cancelled = true;
        if (emptied) this.replaceChildren(lineItems);
      };

      startViewTransition(() => {
        if (cancelled) return;
        document.getElementById('cart-drawer-heading')?.remove();
        lineItems.append(...this.childNodes);
        this.replaceChildren(clone);
        emptied = true;
        if (hadFocus || CartItemsComponent.#isFocusLost()) this.#focusRemaining(0);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((removedRow) => {
      const hide = () => {
        // The removal may already have failed and been undone, or the row replaced by a re-render.
        if (this.#removedRows.get(key) !== rowsToRemove) return;

        const focusWasInside = removedRow.contains(document.activeElement);
        removedRow.classList.remove('removing');
        removedRow.classList.add('hidden');
        if (focusWasInside || (hadFocus && CartItemsComponent.#isFocusLost())) this.#focusRemaining(index);
      };

      if (prefersReducedMotion()) return hide();

      removedRow.style.setProperty('--row-height', `${removedRow.clientHeight}px`);
      removedRow.classList.add('removing');

      // Hide the row after the animation ends
      onAnimationEnd(removedRow, hide);
    });
  }

  /**
   * Shows again the rows hidden for a removal the server did not confirm.
   * @param {string} key - The line key.
   */
  #restoreRemovedRows(key) {
    const rows = this.#removedRows.get(key);
    if (!rows) return;

    this.#removedRows.delete(key);
    this.#restoreEmptiedCart?.();
    this.#restoreEmptiedCart = null;

    for (const row of rows) {
      row.classList.remove('removing', 'hidden');
      row.style.removeProperty('--row-height');
    }
  }

  /**
   * Updates the quantity.
   *
   * Updates are sent one at a time through a queue shared by every cart on the page. A line is identified
   * by its key, read from the row, because its position changes as other lines are removed. `line` is
   * used only when no key is available.
   *
   * @param {Object} config - The config.
   * @param {number} config.line - The 1-based position the line had when it was rendered.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {string} [config.key] - The line item key.
   */
  async updateQuantity(config) {
    const key = config.key ?? this.#rows[config.line - 1]?.dataset.key ?? '';
    const queueKey = key || `line:${config.line}`;
    const row = key ? this.#rowForKey(key) : undefined;
    const focus = {
      // Read now: by the time the request is sent, the control that was used may be disabled or gone.
      // Only real keyboard focus counts. A tap that focuses nothing must not end with focus being placed.
      wasInRow: Boolean(row?.contains(document.activeElement)),
      index: row ? this.#visibleRows.indexOf(row) : -1,
    };

    // A change still waiting its turn for this line is replaced, not followed by a second request.
    if (cartMutationQueue.isWaiting(queueKey)) {
      return cartMutationQueue.enqueue(queueKey, { ...config, key, focus }, () => Promise.resolve());
    }

    let completeQuantityUpdate = () => {};
    /** @type {Promise<void>} */
    const quantityUpdate = new Promise((resolve) => {
      completeQuantityUpdate = () => resolve();
    });
    CartItemsComponent.#pendingQuantityUpdates.add(quantityUpdate);

    this.#activeUpdates += 1;
    this.#disableCartItems();

    try {
      await cartMutationQueue.enqueue(queueKey, { ...config, key, focus }, async (latest) => {
        this.#sendingQuantities.set(queueKey, latest.quantity);
        try {
          await this.#sendQuantityUpdate(latest);
        } finally {
          this.#sendingQuantities.delete(queueKey);
        }
      });
    } finally {
      this.#activeUpdates -= 1;
      if (this.#activeUpdates === 0) this.#enableCartItems();

      CartItemsComponent.#pendingQuantityUpdates.delete(quantityUpdate);
      completeQuantityUpdate();

      this.#reconcileIfNeeded();
    }
  }

  /**
   * Sends one quantity change and applies the server's answer.
   * @param {Object} config - The config.
   * @param {number} config.line - The 1-based position the line had when it was rendered.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {string} config.key - The line item key, or an empty string.
   * @param {{ wasInRow: boolean, index: number }} config.focus - Where focus was when the change was asked for.
   */
  async #sendQuantityUpdate(config) {
    const { line, quantity, key, focus } = config;
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    // An earlier update re-rendered the cart and this line is no longer in it under this key. (A line taken
    // off screen for this very removal is still in the cart.) The change is not sent, because nothing says
    // which line it would reach.
    if (key && !this.#rowForKey(key) && !this.#removedRows.has(key)) {
      const missing = await resolveMissingLine({
        removedHere: this.#confirmedRemovals.has(key),
        reconcile: () => this.#reconcile(),
      });

      // Unless the shopper's own removal already dealt with the line, say that the change was not made.
      if (missing.outcome === 'retry') {
        if (!missing.confirmed) this.#needsReconcile = true;
        this.#showNotice(this.dataset.lineChanged || 'Your bag has changed. Please try again.');
      }
      return;
    }

    // An add to cart from elsewhere is still in flight, so the two responses could arrive out of order.
    if (this.#externalUpdates > 0) this.#needsReconcile = true;

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();

    try {
      const { cartTotal } = this.refs;

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionsToUpdate = new Set([this.sectionId]);
      for (const item of cartItemsComponents) {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      }

      for (const sectionId of sectionsToUpdate) {
        sectionRenderer.abortRender(sectionId);
      }

      // The key names one line even when the same variant is on several lines; a position does not survive
      // another line being removed first.
      const body = JSON.stringify({
        ...(key ? { id: key } : { line }),
        quantity,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });

      cartTotal?.shimmer();

      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
          context: 'cart',
          lines: [{ id: key, quantity }],
          promise: deferredUpdatePromise.promise,
        })
      );

      const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
      const responseText = await response.text();
      const parsedResponseText = JSON.parse(responseText);

      resetShimmer(this);

      const errorMessage = CartItemsComponent.#errorMessageFrom(parsedResponseText, response);
      if (errorMessage) {
        // The server refused the change, so the cart is as it was: undo what was shown ahead of it.
        this.#restoreRemovedRows(key);
        this.#showLineError(key, line, errorMessage);
        if (quantity === 0) this.#refocusRestoredRow(key, focus);
        deferredUpdatePromise.reject(new Error(errorMessage));

        this.dispatchEvent(
          new CartErrorEvent({
            error: errorMessage,
            code: 'INVALID',
          })
        );
        return;
      }

      const sections = /** @type {Record<string, string> | null | undefined} */ (parsedResponseText.sections);
      const sectionHTML = sections?.[this.sectionId];
      const parsedItemCount = Number(parsedResponseText.item_count);
      let newCartItemCount = Number.isFinite(parsedItemCount) ? parsedItemCount : 0;

      // Update data-cart-quantity for all matching variants
      this.#updateQuantitySelectors(parsedResponseText);

      if (sectionHTML) {
        const newSectionHTML = new DOMParser().parseFromString(sectionHTML, 'text/html');

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : newCartItemCount;
      }

      deferredUpdatePromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
        detail: {
          sections: sections ?? undefined,
          items: parsedResponseText.items,
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          didError: false,
        },
      });

      this.#removedRows.delete(key);
      this.#restoreEmptiedCart = null;
      if (quantity === 0 && key) this.#confirmedRemovals.add(key);

      if (sectionHTML) {
        sectionRenderer.abortRender(this.sectionId);
        await morphSection(this.sectionId, sectionHTML, {
          mode: this.isDrawer ? 'hydration' : 'full',
        });
        this.#afterRender();
      } else {
        await this.#refreshCartSection();
      }

      this.#restoreFocus(key, focus);
      this.#reportAdjustedQuantity(key, quantity, parsedResponseText.items);
    } catch (error) {
      // No usable answer: the request may or may not have reached the server.
      console.error(error);
      resetShimmer(this);
      deferredUpdatePromise.reject(error);

      // Back to the last confirmed display first, whatever happens next.
      this.#restoreRemovedRows(key);

      // Then read what the server actually holds. The change itself is not sent again.
      const outcome = await resolveUnansweredChange({
        reconcile: () => this.#reconcile(),
        wasApplied: () => {
          const row = key ? this.#rowForKey(key) : undefined;
          if (quantity === 0) return !row;
          const input = /** @type {HTMLInputElement | null | undefined} */ (row?.querySelector('input[data-cart-line]'));
          return Number(input?.defaultValue) === quantity;
        },
      });

      if (outcome === 'not-applied') {
        // The cart was read and the change is not in it.
        this.#showLineError(key, line, this.dataset.updateError || 'Your bag could not be updated. Please try again.');
        if (quantity === 0) this.#refocusRestoredRow(key, focus);
      } else if (outcome === 'unknown') {
        // The cart could not be read either (still offline, for instance). The line shows its last confirmed
        // quantity, the message says the change is unconfirmed rather than refused, and the cart is read
        // again when the connection returns or after the next change.
        this.#needsReconcile = true;
        this.#showLineError(
          key,
          line,
          this.dataset.updateUnconfirmed || 'This change could not be confirmed. Check your connection and try again.'
        );
        if (quantity === 0) this.#refocusRestoredRow(key, focus);
      }

      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Failed to update cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Reads the reason a cart change was refused, in the server's own words.
   * @param {any} parsedResponse - The parsed response body.
   * @param {Response} response - The response.
   * @returns {string} The message, or an empty string when the change was accepted.
   */
  static #errorMessageFrom(parsedResponse, response) {
    const { errors, description, message, status } = parsedResponse ?? {};

    if (typeof errors === 'string' && errors) return errors;

    const failed = Boolean(errors) || !response.ok || (typeof status === 'number' && status >= 400);
    if (!failed) return '';

    if (typeof description === 'string' && description) return description;
    if (typeof message === 'string' && message) return message;
    return 'Cart update failed';
  }

  /**
   * Shows a message on a line and puts its quantity back to the last confirmed value.
   * @param {string} key - The line item key.
   * @param {number} line - The 1-based position the line had when it was rendered.
   * @param {string} message - The message.
   */
  #showLineError(key, line, message) {
    const row = key ? this.#rowForKey(key) : this.#rows[line - 1];
    if (!row) return;

    const quantityInput = /** @type {HTMLInputElement | null} */ (row.querySelector('input[data-cart-line]'));
    if (quantityInput) {
      quantityInput.removeAttribute('data-skip-value-update');
      quantityInput.value = quantityInput.defaultValue;
    }

    const container = row.querySelector('.cart-items__error');
    const text = row.querySelector('.cart-item__error-text');
    if (!container || !text) return;

    text.textContent = message;
    container.classList.remove('hidden');

    this.#updateCartQuantitySelectorButtonStates();
  }

  /**
   * Shows a message that belongs to the whole cart rather than to one line. The next re-render clears it.
   * @param {string} message - The message.
   */
  #showNotice(message) {
    let notice = this.querySelector(':scope > .cart-items__notice');

    if (!notice) {
      notice = document.createElement('p');
      notice.className = 'cart-items__notice';
      notice.setAttribute('role', 'alert');
      this.prepend(notice);
    }

    notice.textContent = message;
    notice.classList.remove('hidden');
  }

  /**
   * Returns focus to the Remove control of a line whose removal failed, so it can be tried again.
   * @param {string} key - The line item key.
   * @param {{ wasInRow: boolean, index: number }} focus - Where focus was when the removal was asked for.
   */
  #refocusRestoredRow(key, focus) {
    if (!focus.wasInRow || !key) return;

    const remove = /** @type {HTMLElement | null | undefined} */ (
      this.#rowForKey(key)?.querySelector('.cart-items__remove:not([hidden])')
    );
    remove?.focus({ preventScroll: true });
  }

  /**
   * Says so when the server accepted a change but set a different quantity from the one asked for.
   * @param {string} key - The line item key.
   * @param {number} requested - The quantity asked for.
   * @param {Array<{ key: string, quantity: number }>} [items] - The cart items in the response.
   */
  #reportAdjustedQuantity(key, requested, items) {
    const template = this.dataset.quantityAdjusted;
    if (!key || requested === 0 || !template || !Array.isArray(items)) return;

    const item = items.find((cartItem) => cartItem.key === key);
    if (!item || item.quantity === requested) return;

    this.#showLineError(key, 0, template.replace('[quantity]', String(item.quantity)));
  }

  /**
   * Re-renders the cart from the server and brings the header count into line with it.
   * @returns {Promise<boolean>} Whether the cart on screen now matches the server.
   */
  async #reconcile() {
    if (!this.dataset.sectionId) return false;

    try {
      const html = await sectionRenderer.renderSection(this.sectionId, {
        cache: false,
        mode: this.isDrawer ? 'hydration' : 'full',
      });
      // A render that was cancelled resolves with nothing and changes nothing.
      if (!html) return false;
    } catch (error) {
      return false;
    }

    this.#afterRender();

    const renderedCount = this.querySelector('[ref="cartItemCount"]')?.textContent;
    const itemCount = renderedCount ? parseInt(renderedCount, 10) : 0;
    if (Number.isInteger(itemCount)) {
      for (const cartIcon of document.querySelectorAll('cart-icon')) {
        /** @type {any} */ (cartIcon).renderCartBubble?.(itemCount, false);
      }
    }

    return true;
  }

  /**
   * After our update and an outside cart change overlapped, fetches the cart once everything has settled.
   */
  #reconcileIfNeeded() {
    if (!this.#needsReconcile) return Promise.resolve(true);
    if (this.#activeUpdates > 0 || this.#externalUpdates > 0) return Promise.resolve(false);

    // One read at a time; anyone asking meanwhile gets the same answer.
    this.#reconcilePromise ??= this.#reconcile().then((confirmed) => {
      this.#reconcilePromise = null;
      // Still unreadable: keep the flag, so the next chance (back online, or after the next change) tries again.
      if (confirmed) this.#needsReconcile = false;
      return confirmed;
    });

    return this.#reconcilePromise;
  }

  /**
   * Reads the cart from Shopify and shows it: line items, totals, extras and the header count.
   * Nothing is sent that could change the cart.
   * @returns {Promise<boolean>} Whether the cart was read. False while a cart change is still in flight.
   */
  refreshFromServer() {
    this.#needsReconcile = true;
    return this.#reconcileIfNeeded();
  }

  /**
   * Runs after every re-render of the cart.
   */
  #afterRender() {
    // A row hidden for a removal that is still waiting its turn must not reappear with this render.
    for (const [key, rows] of this.#removedRows) {
      const row = this.#rowForKey(key);
      if (!row) continue;
      if (!rows.includes(row)) rows.push(row);
      row.classList.add('hidden');
    }

    this.#updateCartQuantitySelectorButtonStates();

    // A re-render writes the controls afresh. If another change is still waiting, they stay unavailable.
    if (this.#activeUpdates > 0) setControlsPending(this.#lineControls, true);
  }

  /** @returns {Element[]} The stepper buttons, quantity fields and Remove buttons of every line. */
  get #lineControls() {
    return Array.from(
      this.querySelectorAll('.cart-items__quantity-controls button, .cart-items__quantity-controls input[data-cart-line]')
    );
  }

  /**
   * Puts keyboard focus back on something useful when a re-render or a removal took it away.
   * @param {string} key - The key of the line that was changed.
   * @param {{ wasInRow: boolean, index: number }} focus - Where focus was when the change was asked for.
   */
  #restoreFocus(key, focus) {
    if (!focus.wasInRow || !CartItemsComponent.#isFocusLost()) return;

    const row = key ? this.#rowForKey(key) : undefined;
    // A stepper button at its limit is disabled. Focus goes to the line's other button rather than the
    // number field, which would bring up the keyboard on a touch device.
    const control = /** @type {HTMLElement | null | undefined} */ (
      row && !row.classList.contains('hidden')
        ? row.querySelector(
            'button[name="plus"]:not(:disabled), button[name="minus"]:not(:disabled), .cart-items__remove:not([hidden])'
          )
        : null
    );

    if (control) {
      control.focus({ preventScroll: true });
    } else {
      this.#focusRemaining(focus.index);
    }
  }

  /**
   * Focuses the line now at a position, the last line when there is none there, or the empty cart's action.
   * @param {number} index - The position among the visible rows.
   */
  #focusRemaining(index) {
    const rows = this.#visibleRows;
    const row = rows[Math.max(0, Math.min(index, rows.length - 1))];
    const dialog = this.closest('dialog');

    const target = /** @type {HTMLElement | null | undefined} */ (
      row?.querySelector('.cart-items__remove:not([hidden]), button[name="plus"]:not(:disabled)') ??
        this.querySelector('[data-cart-drawer-empty] :is(button, a[href]), .cart-items__empty-button') ??
        dialog?.querySelector('.theme-drawer__close-button')
    );

    target?.focus();
  }

  /** @returns {boolean} Whether nothing useful has focus: the control that had it was removed, hidden or disabled. */
  static #isFocusLost() {
    const active = document.activeElement;

    return (
      !active ||
      active === document.body ||
      active instanceof HTMLDialogElement ||
      !active.isConnected ||
      active.matches(':disabled') ||
      active.closest('.hidden') !== null
    );
  }

  /**
   * Read from the document each time: `refs` are refreshed after the DOM changes, not during.
   * @returns {HTMLElement[]} The line item rows, in cart order.
   */
  get #rows() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('.cart-items__table-row[data-key]')));
  }

  /** @returns {HTMLElement[]} The line item rows that are on screen and not being removed. */
  get #visibleRows() {
    return this.#rows.filter((row) => !row.classList.contains('hidden') && !row.classList.contains('removing'));
  }

  /**
   * @param {string} key - The line item key.
   * @returns {HTMLElement | undefined} The row for a line item key.
   */
  #rowForKey(key) {
    return this.#rows.find((row) => row.dataset.key === key);
  }

  /**
   * @param {Element | null} element - An element inside the row, when known.
   * @param {number} line - The 1-based position the line had when it was rendered.
   * @returns {HTMLElement | undefined} The row a control belongs to.
   */
  #rowFor(element, line) {
    const row = element?.closest('[data-key]');
    if (row instanceof HTMLElement && this.contains(row)) return row;

    return this.#rows[line - 1];
  }

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    if (event.promise) {
      // If one of our own updates is in flight too, the two responses could be applied out of order.
      if (this.#activeUpdates > 0) this.#needsReconcile = true;
      this.#externalUpdates += 1;

      /** @param {boolean} answered */
      const settled = (answered) => {
        this.#externalUpdates -= 1;
        // An add that got no answer may or may not have gone through. It is never sent again from here:
        // the cart is read instead, now or once the connection returns.
        if (!answered) this.#needsReconcile = true;
        this.#reconcileIfNeeded();
      };
      event.promise.then(
        () => settled(true),
        () => settled(false)
      );
    }

    event.promise
      ?.then(async ({ detail }) => {
        // A refused "Add something extra" leaves the cart unchanged. Re-rendering would wipe the card's
        // message and the option the shopper chose, so the drawer is left as it is.
        if (detail?.didError && event.target instanceof Element && event.target.closest('[data-extras-card]')) {
          return;
        }

        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          sectionRenderer.abortRender(this.sectionId);
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer) {
            startViewTransition(() => {
              morphSection(this.sectionId, cartItemsHtml, morphOptions);
            }, ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#afterRender();
        } else {
          this.#refreshCartSection(morphOptions);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
    this.setAttribute('aria-busy', 'true');
    setControlsPending(this.#lineControls, true);
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
    this.removeAttribute('aria-busy');
    setControlsPending(this.#lineControls, false);
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    // Discounts such as Buy X Get Y can split one variant across several cart lines.
    /** @type {Map<string, number>} */
    const quantitiesByVariant = new Map();
    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      quantitiesByVariant.set(variantId, (quantitiesByVariant.get(variantId) ?? 0) + item.quantity);
    }

    for (const [variantId, quantity] of quantitiesByVariant) {
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }

  /**
   * `getEntriesByType` is typed as the `PerformanceEntry` base, which has no `type`, and the DOM
   * lib has no per-string overload for `'navigation'`. `entryType` is the spec's discriminant and
   * lives on the base, so this narrows at runtime instead of asserting the way a cast would.
   *
   * @param {PerformanceEntry | undefined} entry
   * @returns {entry is PerformanceNavigationTiming}
   */
  static #isNavigationTiming(entry) {
    return entry?.entryType === 'navigation';
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
